import {PaymentProviderResponse} from '@gatewee/types/payment';
import {Withdrawal, WithdrawalStatus} from '@gatewee/types/withdrawal';

export interface WithdrawalStatusErrorTranslate {
  instant(key: string): string;
}

export function extractPaymentMetadataError(
  metadata?: PaymentProviderResponse<unknown, unknown> | null,
): string | null {
  if (!metadata) {
    return null;
  }

  const error = metadata.error?.trim();
  if (error) {
    return error;
  }

  const providerErrorMessage = metadata.providerErrorMessage?.trim();
  if (providerErrorMessage) {
    return providerErrorMessage;
  }

  return null;
}

export function getLatestWithdrawalReasonKey(
  withdrawal: Withdrawal,
): string | undefined {
  const history = withdrawal.statusHistory;
  if (!history?.length) return undefined;

  return [...history]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .find(entry => entry.reasonKey)?.reasonKey;
}

/** Reason key from the statusHistory entry that matches the withdrawal's current status. */
export function getWithdrawalReasonKeyForCurrentStatus(
  withdrawal: Withdrawal,
): string | undefined {
  const history = withdrawal.statusHistory;
  if (!history?.length) {
    return undefined;
  }

  const currentStatus = withdrawal.status;
  const matchingEntry = [...history]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .find(
      entry =>
        entry.status === currentStatus &&
        typeof entry.reasonKey === 'string' &&
        entry.reasonKey.trim().length > 0,
    );

  return matchingEntry?.reasonKey?.trim();
}

export function resolveWithdrawalStatusReasonKey(
  withdrawal: Withdrawal,
): string | undefined {
  const fromCurrentStatus = getWithdrawalReasonKeyForCurrentStatus(withdrawal);
  if (fromCurrentStatus) {
    return fromCurrentStatus;
  }

  const metadataReason = withdrawal.paymentMetadata?.statusReason;
  if (typeof metadataReason === 'string' && metadataReason.trim()) {
    return metadataReason.trim();
  }

  return undefined;
}

export function translateStatusReasonDescription(
  reasonKey: string | undefined,
  translate: WithdrawalStatusErrorTranslate,
): string | null {
  if (!reasonKey?.trim()) {
    return null;
  }

  const key = `features.statusReason.${reasonKey}.description`;
  const translated = translate.instant(key);
  if (translated !== key) {
    return translated;
  }

  return null;
}

export function isWithdrawalStatusError(withdrawal: Withdrawal): boolean {
  return (
    withdrawal.status === WithdrawalStatus.FAILED ||
    withdrawal.status === WithdrawalStatus.CANCELED
  );
}

export function getWithdrawalStatusErrorMessage(
  withdrawal: Withdrawal,
  translate: WithdrawalStatusErrorTranslate,
): string {
  const reasonKey = resolveWithdrawalStatusReasonKey(withdrawal);
  const translatedReason = translateStatusReasonDescription(
    reasonKey,
    translate,
  );
  if (translatedReason) {
    return translatedReason;
  }

  const providerError = extractPaymentMetadataError(withdrawal.paymentMetadata);
  if (providerError) {
    return providerError;
  }

  const fallbackKey =
    withdrawal.status === WithdrawalStatus.CANCELED
      ? 'features.withdrawals.table.statusError.canceled'
      : 'features.withdrawals.table.statusError.failed';

  return translate.instant(fallbackKey);
}
